import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Smartphone, 
  Calendar, 
  Wallet, 
  MapPin, 
  User, 
  Phone, 
  ShieldCheck, 
  CheckCircle2, 
  XCircle, 
  Info,
  Clock,
  Printer,
  Loader2
} from 'lucide-react';
import { api } from "../services/api";

const PRIMARY = "#363C90";

const OrderDetails: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // --- REUSABLE GB TO TB CONVERSION UTILITY ---
  const formatStorage = (storageValue: any): string => {
    const storageStr = String(storageValue || "");
    const numericValue = parseInt(storageStr.replace(/\D/g, ""), 10);
    if (isNaN(numericValue)) return storageStr;

    if (numericValue >= 1024) {
      const tbValue = numericValue / 1024;
      return `${tbValue} TB`;
    }
    return `${numericValue} GB`;
  };

  // --- DYNAMIC TEXT-SPECS CONVERSION HELPER ---
  // Ye pure specifications string me se 1024 GB ko track karke TB me convert karega
  const parseSpecsText = (specsStr: string): string => {
    if (!specsStr) return "";
    return specsStr.replace(/(\d+)\s*(GB|gb)/g, (match, num) => {
      const numeric = parseInt(num, 10);
      if (numeric >= 1024) {
        return `${numeric / 1024} TB`;
      }
      return match;
    });
  };

  const fetchOrderDetails = async () => {
    try {
      const data = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch (err) {
      console.error("Detail Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) fetchOrderDetails();
  }, [orderId]);

  if (loading) return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="animate-spin text-[#363C90]" size={40} />
      <p className="font-bold text-slate-400">Loading Order Details...</p>
    </div>
  );

  if (!order) return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center p-6">
      <XCircle size={60} className="text-slate-200 mb-4" />
      <h2 className="text-2xl font-black text-slate-900">Order Not Found</h2>
      <button onClick={() => navigate('/orders')} className="mt-6 text-[#363C90] font-black underline">Back to My Orders</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 px-6">
      <div className="max-w-[1000px] mx-auto">
        
        {/* Header Navigation */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 hover:bg-slate-50 transition-all">
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-black text-slate-900">Order #{order.id}</h1>
              <p className="text-slate-400 text-sm font-bold">{order.date}</p>
            </div>
          </div>
          <button onClick={() => window.print()} className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-all">
            <Printer size={16} /> Print Receipt
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT COLUMN: Main Details */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Status & OTP Section */}
            <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between mb-8">
                <span className="px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest" style={{ backgroundColor: (order.color || PRIMARY) + '15', color: order.color || PRIMARY, border: `1px solid ${order.color || PRIMARY}30` }}>
                  {order.status}
                </span>
                <Smartphone size={24} className="text-slate-200" />
              </div>

              <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-900 mb-1">{order.device_model}</h2>
                {/* YAHAN CHANGES APPLIED: Pure specs line string ko direct evaluate kiya */}
                <p className="text-slate-500 font-medium">{parseSpecsText(order.device_specs)}</p>
                <p className="text-4xl font-black text-[#363C90] mt-4">₹{order.price}</p>
              </div>

              {order.otp && (
                <div className="bg-[#ECFDF5] border-2 border-dashed border-[#A7F3D0] rounded-[24px] p-6 text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <ShieldCheck size={18} className="text-[#059669]" />
                    <span className="text-[11px] font-black text-[#065F46] uppercase tracking-tighter">Verification OTP</span>
                  </div>
                  <div className="text-5xl font-black text-[#059669] tracking-[12px] mb-4 ml-[12px]">{order.otp}</div>
                  <div className="flex items-start gap-3 bg-white/50 p-4 rounded-xl text-left">
                    <Info size={16} className="text-[#065F46] mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-[#065F46] font-medium leading-relaxed">
                      Share this code <span className="font-black">ONLY</span> after you have received the payment in your account.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Evaluation Summary */}
            <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">Device Evaluation</h3>
              <div className="space-y-6">
                {order.evaluation?.map((cat: any, idx: number) => (
                  <div key={idx} className="border-t border-slate-50 pt-6 first:border-0 first:pt-0">
                    <p className="text-[11px] font-black text-[#363C90] uppercase mb-4 tracking-wider">{cat.title}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {cat.items.map((item: any, i: number) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            {item.is_positive ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-red-400" />}
                            <span className="text-xs font-bold text-slate-600">{item.question}</span>
                          </div>
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded ${item.is_positive ? 'text-emerald-700 bg-emerald-100' : 'text-red-700 bg-red-100'}`}>
                            {formatStorage(item.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Sidebar Info */}
          <div className="space-y-6">
            
            {/* Pickup & Agent Card */}
            <div className="bg-white rounded-[32px] p-6 border border-slate-100 shadow-sm">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">Pickup Details</h3>
              
              <div className="space-y-5">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-[#363C90] shrink-0">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-[#363C90] uppercase tracking-tighter">Address</p>
                    <p className="text-sm font-bold text-slate-800 leading-snug">{order.address?.full}</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-[#363C90] shrink-0">
                    <Calendar size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-[#363C90] uppercase tracking-tighter">Scheduled Time</p>
                    <p className="text-sm font-bold text-slate-800">{order.pickup_time || 'TBD'}</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-[#363C90] shrink-0">
                    <Wallet size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-[#363C90] uppercase tracking-tighter">Payment Mode</p>
                    <p className="text-sm font-bold text-slate-800">{order.payment_method}</p>
                  </div>
                </div>
              </div>

              {/* Assigned Executive */}
              <div className="mt-8 pt-8 border-t border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Assigned Executive</p>
                {order.pickup_man ? (
                  <div className="flex items-center justify-between p-4 bg-[#363C90] rounded-[24px] text-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <User size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-black">{order.pickup_man.name}</p>
                        <p className="text-[10px] text-white/70">Executive assigned</p>
                      </div>
                    </div>
                    <a href={`tel:${order.pickup_man.phone}`} className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                      <Phone size={18} />
                    </a>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 border border-dashed border-slate-200 rounded-2xl bg-slate-50">
                    <Clock size={16} className="text-slate-400 animate-pulse" />
                    <p className="text-xs font-bold text-slate-400">Assignment in progress...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Support Box */}
            <div className="bg-indigo-50 rounded-[32px] p-6 text-center">
              <h4 className="text-sm font-black text-[#363C90] mb-2">Need Help?</h4>
              <p className="text-xs text-indigo-400 font-medium mb-4">Contact our support if you face any issues during pickup.</p>
              <button className="w-full py-3 bg-white text-[#363C90] rounded-xl text-xs font-black shadow-sm">
                Chat With Support
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;